const CORE_URL = import.meta.env.VITE_GUNSI_CORE_URL
  ?? `${location.protocol}//${location.hostname}:3000`

async function apiFetch(path, options = {}) {
  const res = await fetch(`${CORE_URL}${path}`, {
    credentials: 'include',
    cache: 'no-store',
    headers: options.body !== undefined ? { 'Content-Type': 'application/json' } : {},
    ...options,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })
  if (!res.ok) throw new Error(`${options.method ?? 'GET'} ${path} → ${res.status}`)
  // Empty body (204 etc) — return null
  const text = await res.text()
  return text ? JSON.parse(text) : null
}

// Loads only settings + categories. Transactions are loaded per-month via loadTransactionsByMonth.
export function loadData() { return apiFetch('/tally/data') }

// yearMonth = "YYYY-MM"
export function loadTransactionsByMonth(yearMonth) {
  const qs = yearMonth ? `?month=${encodeURIComponent(yearMonth)}` : ''
  return apiFetch(`/tally/transactions${qs}`)
}

// from/to = "YYYY-MM-DD". Backend returns tx with date in [from, to).
export function loadTransactionsByRange(from, to) {
  const p = new URLSearchParams()
  if (from) p.set('from', from)
  if (to)   p.set('to', to)
  return apiFetch(`/tally/transactions?${p.toString()}`)
}

// One-off FX conversion (resolves the rate for `date`, fallback applied).
export function fxConvert({ amount, from, to, date }) {
  const p = new URLSearchParams()
  p.set('amount', String(amount))
  if (from) p.set('from', from)
  if (to)   p.set('to', to)
  if (date) p.set('date', date)
  return apiFetch(`/fx/convert?${p.toString()}`)
}

// Full historical series for a currency pair (cross-rate via EUR).
// Returns [{ date: 'YYYY-MM-DD', rate }, ...] ascending.
export function fxHistory({ from, to, since }) {
  const p = new URLSearchParams()
  if (from)  p.set('from', from)
  if (to)    p.set('to', to)
  if (since) p.set('since', since)
  return apiFetch(`/fx/history?${p.toString()}`)
}

// Currency search: matches code/symbol/name/country (all cached server-side).
// Returns up to `limit` currency docs sorted by match relevance.
export function searchCurrencies(q, limit = 50) {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  if (limit) params.set('limit', String(limit))
  const qs = params.toString()
  return apiFetch(`/currencies/search${qs ? `?${qs}` : ''}`)
}

// Accepts any subset of: { currency, calendar, yearEra, dayCalendar }
export function saveSettings(settings) {
  return apiFetch('/tally/settings', { method: 'PUT', body: settings })
}

export function saveCategories(categories) {
  return apiFetch('/tally/categories', { method: 'PUT', body: { categories } })
}

// ── Transactions: granular CRUD ───────────────────────────────────────────

export function createTransaction(tx) {
  return apiFetch('/tally/transactions', { method: 'POST', body: tx })
}

export function patchTransaction(id, patch) {
  return apiFetch(`/tally/transactions/${id}`, { method: 'PATCH', body: patch })
}

export function deleteTransaction(id) {
  return apiFetch(`/tally/transactions/${id}`, { method: 'DELETE' })
}

// Bulk replace — used only by LS→DB migration on first boot
export function replaceAllTransactions(transactions) {
  return apiFetch('/tally/transactions', { method: 'PUT', body: { transactions } })
}

// ── Names management (desktop /manage page) ───────────────────────────────
export function loadNames() {
  return apiFetch('/tally/names')
}

// Rename one (`from: string`) or many (`from: string[]`) tx-name(s) to a
// single target. Optional `appendToNote` is concatenated to existing notes
// (or used as-is if note empty).
export function renameNames({ from, to, appendToNote }) {
  return apiFetch('/tally/names/rename', {
    method: 'POST',
    body: { from, to, appendToNote },
  })
}

// Strip the `name` field entirely from every tx matching the given names.
export function clearNames(names) {
  return apiFetch('/tally/names/clear', { method: 'POST', body: { names } })
}

// ── Ledger integration (backfill on toggle-on, wipe on toggle-off) ────────
export function syncLedgerBackfill() {
  return apiFetch('/tally/sync-ledger', { method: 'POST' })
}
export function syncLedgerWipe() {
  return apiFetch('/tally/sync-ledger/wipe', { method: 'POST' })
}
